import { useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import Typography from "@mui/material/Typography"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import useUploadImage from "../UploadImage/useUploadImage"
import ImagePreview from "../ImagePreview"
import styles from "./UploadImageV2.module.scss"

/**
 * Image uploader that shows every slot up front — filled ones as thumbnails,
 * the rest as dashed placeholders — and folds the count into the button label.
 *
 * Separate from UploadImage so the commissioning lane's existing image fields
 * keep their current look; the upload engine (useUploadImage) is shared, so
 * presign / S3 / doc_id handling lives in one place.
 */
type UploadImageV2Props = {
    name: string
    label?: any
    /** Slots shown, and the cap enforced on selection. */
    maxFiles?: number
    required?: boolean
    disabled?: boolean
    error?: boolean
    helperText?: any
    selectedFiles?: any
    setSelectedFiles: any
    setDeletedFiles?: any
    imagesData?: { doc_id: number; url: string }[]
    isFetching?: boolean
    reportType?: string
    /** Omit to hide the instruction icon rather than render one that throws. */
    handleModal?: (name: string) => void
    accept?: string
    acceptLabel?: string
    /** Per-file size cap in bytes; 0 disables the check. */
    maxBytes?: number
    /** "grid" shows square slots; "file" shows a single file chip (name, size, type). */
    variant?: "grid" | "file"
}

const KIND: Record<string, string> = {
    "application/pdf": "PDF",
    "image/png": "PNG",
    "image/jpeg": "JPG",
}

const fileKind = (type?: string, name?: string) =>
    KIND[type ?? ""] ?? (name?.split(".").pop() ?? "FILE").toUpperCase().slice(0, 4)

const fileSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return ""
    return bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const UploadImageV2 = (props: UploadImageV2Props) => {
    const maxFiles = props.maxFiles ?? 3
    const accept = props.accept ?? "image/png,image/jpeg"
    const acceptLabel = props.acceptLabel ?? "PNG / JPEG"
    const isFileVariant = props.variant === "file"

    // The `accept` list drives both the file picker and the validation inside
    // the hook, so a PDF group cannot accept in one place and reject in the other.
    const allowedTypes = accept
        .split(",")
        .map(type => type.trim())
        .filter(Boolean)

    const { states, functions } = useUploadImage(
        props.name,
        props.setSelectedFiles,
        props.imagesData,
        props.setDeletedFiles,
        props.isFetching,
        {
            reportType: props.reportType,
            maxFiles,
            maxBytes: props.maxBytes,
            allowedTypes,
            awaitUpload: true,
        }
    )

    const isUploading = states.isUploading || states.isCompressing

    // A saved link can fail: the presigned URL expires after an hour, and a
    // doc whose S3 object was never written 404s with NoSuchKey. Fall back to
    // the label rather than leaving a broken-image icon.
    const [brokenDocIds, setBrokenDocIds] = useState<Record<string, boolean>>({})

    // Which thumbnail is open in the large view; null when none is.
    const [preview, setPreview] = useState<{ url: string; name?: string } | null>(null)

    const images = states.images ?? []
    const atLimit = images.length >= maxFiles
    const emptySlots = Math.max(0, maxFiles - images.length)

    /**
     * A thumbnail opens the large view only when there is an image to show:
     * a PDF has no preview, and a saved doc whose link expired or 404d is
     * already flagged in `brokenDocIds`.
     */
    const canPreview = (image: any) =>
        !!image?.url &&
        !brokenDocIds[image?.doc_id] &&
        (!image?.type || image.type.startsWith("image/"))

    // `label` may be JSX (the electricity-bill field composes a badge into it),
    // so it is only safe as text when it actually is a string.
    const labelText = typeof props.label === "string" ? props.label : ""

    const openPreview = (image: any) =>
        setPreview({ url: image.url, name: image?.name || labelText })

    return (
        <Box className={styles.upload_v2}>
            <FormControl sx={{ padding: 0, width: "100%" }}>
                <FormLabel id={props.name} className={styles.upload_v2__label}>
                    <Box className={styles.upload_v2__label_row}>
                        <Typography>{props.label}</Typography>
                        {props.required && <Typography className={styles.upload_v2__required}>*</Typography>}
                        {props.handleModal && (
                            <FontAwesomeIcon
                                icon={faCircleInfo}
                                className={styles.upload_v2__info}
                                onClick={() => props.handleModal?.(props.name)}
                            />
                        )}
                    </Box>
                </FormLabel>

                {isFileVariant ? (
                    <Box className={styles.upload_v2__file}>
                        {images.map((image: any, index: number) => (
                            <Box key={image?.doc_id ?? `file-${index}`} className={styles.fchip}>
                                <Box className={styles.fchip__kind}>
                                    {fileKind(image?.type, image?.name)}
                                </Box>
                                <Box className={styles.fchip__meta}>
                                    <Typography className={styles.fchip__name}>
                                        {image?.name ?? "Attached file"}
                                    </Typography>
                                    <Typography className={styles.fchip__size}>
                                        {image?.isExisting ? "Saved" : fileSize(image?.size)}
                                    </Typography>
                                </Box>
                                <button
                                    type="button"
                                    aria-label="Remove file"
                                    className={styles.fchip__remove}
                                    onClick={() => functions.removeImage(image?.doc_id)}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </Box>
                        ))}
                    </Box>
                ) : (
                <Box className={styles.upload_v2__thumbs}>
                    {images.map((image: any, index: number) => (
                        <Box
                            key={image?.doc_id ?? `image-${index}`}
                            // `thumb_saved` is the padded "Saved" placeholder; a
                            // saved image with a url renders like any thumbnail.
                            className={
                                image?.isExisting && (!image?.url || brokenDocIds[image?.doc_id])
                                    ? styles.thumb_saved
                                    : styles.thumb
                            }
                        >
                            {/* An already-saved image has a `url` from the report's
                                `images` (its `view` link), so show the photo
                                itself — coming back to a filled page used to show
                                only the word "Saved". */}
                            {image?.isExisting ? (
                                image?.url && !brokenDocIds[image?.doc_id] ? (
                                    <img
                                        src={image.url}
                                        alt=""
                                        className={styles.thumb__img}
                                        onClick={() => canPreview(image) && openPreview(image)}
                                        onError={() =>
                                            setBrokenDocIds(prev => ({
                                                ...prev,
                                                [image?.doc_id]: true,
                                            }))
                                        }
                                    />
                                ) : (
                                    <Typography className={styles.thumb__saved_text}>Saved</Typography>
                                )
                            ) : image?.type?.startsWith("image/") ? (
                                <img
                                    src={image?.url}
                                    alt=""
                                    className={styles.thumb__img}
                                    onClick={() => canPreview(image) && openPreview(image)}
                                />
                            ) : (
                                // A PDF has no thumbnail; name the format instead
                                // of rendering a broken <img>.
                                <Typography className={styles.thumb__saved_text}>
                                    {fileKind(image?.type, image?.name)}
                                </Typography>
                            )}
                            <button
                                type="button"
                                aria-label="Remove image"
                                className={styles.thumb__remove}
                                onClick={() => functions.removeImage(image?.doc_id)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </Box>
                    ))}

                    {/* The placeholder is the obvious thing to tap, so it opens
                        the picker as well as the button below it. */}
                    {Array.from({ length: emptySlots }).map((_, index) => (
                        <button
                            key={`empty-${index}`}
                            type="button"
                            aria-label={labelText ? `Add ${labelText}` : "Add image"}
                            className={styles.thumb_empty}
                            disabled={props.disabled || isUploading}
                            onClick={functions.handleUploadClick}
                        />
                    ))}
                </Box>
                )}

                <input
                    type="file"
                    ref={states.fileInputRef}
                    accept={accept}
                    multiple={maxFiles > 1}
                    hidden
                    onChange={functions.handleFilesChange}
                />

                <Button
                    fullWidth
                    type="button"
                    className={styles.upload_v2__button}
                    disabled={props.disabled || isUploading || (!isFileVariant && atLimit)}
                    onClick={functions.handleUploadClick}
                >
                    {states.isCompressing
                        ? "Compressing…"
                        : isUploading
                        ? "Uploading…"
                        : isFileVariant
                          ? images.length
                              ? "Replace file"
                              : "Upload"
                          : images.length
                            ? `Upload more (${images.length}/${maxFiles})`
                            : "Upload"}
                </Button>

                <Box className={styles.upload_v2__hint}>
                    <Typography>
                        {maxFiles === 1 ? "Single file" : `Select up to ${maxFiles} images`}
                    </Typography>
                    <Typography>{acceptLabel}</Typography>
                </Box>

                {props.error && props.helperText && (
                    <Typography className="error_message">{props.helperText}</Typography>
                )}
            </FormControl>

            <ImagePreview
                url={preview?.url}
                name={preview?.name}
                onClose={() => setPreview(null)}
            />
        </Box>
    )
}

export default UploadImageV2
